// saas-monetization-plans v2 — WU-L: AssignPlanDialog.
//
// Lets a Super Admin assign (or reassign) a Plan to an Organization
// (design §8, PUT /super-admin/organizations/:orgId/plan).
// `initialAdminEmail`/`initialAdminName` are REQUIRED here: since org creation no longer
// collects an admin (Part A of the org-onboarding rework), this dialog is
// the ONLY remaining place that onboards an organization's first Org Admin
// AND its billing contact — required by the backend on the very first
// assignment for an org with no billing relationship yet, and harmless to
// always require here rather than guess whether this is a first assignment.

import { ApiError, assignPlanToOrganization, listPlans } from '@jq4race/api-client';
import { Trans, useTranslation } from '@jq4race/i18n';
import type {
  AssignPlanToOrganizationResponse,
  CollectionMethodDto,
  OrganizationResponse,
  PlanResponse,
} from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AssignPlanDialogProps {
  org: OrganizationResponse | null;
  onOpenChange: (open: boolean) => void;
  onAssigned: (result: AssignPlanToOrganizationResponse) => void;
}

export function AssignPlanDialog({ org, onOpenChange, onAssigned }: AssignPlanDialogProps) {
  const { t } = useTranslation('organizations');
  const open = org !== null;
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planId, setPlanId] = useState('');
  const [initialAdminEmail, setInitialAdminEmail] = useState('');
  const [initialAdminName, setInitialAdminName] = useState('');
  // manual-subscription-billing (WU8) — collection method toggle (design §1/§4).
  // Defaults to 'automatic' — the existing Stripe-provisioned flow, unchanged.
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethodDto>('automatic');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  // manual-subscription-billing (WU8) — shown instead of setupUrl when the
  // response confirms a manual (immediate-activation) assignment.
  const [manuallyActivated, setManuallyActivated] = useState(false);

  useEffect(() => {
    if (!open) {
      setPlanId('');
      setInitialAdminEmail('');
      setInitialAdminName('');
      setCollectionMethod('automatic');
      setSubmitting(false);
      setError(null);
      setEmailError(null);
      setNameError(null);
      setSetupUrl(null);
      setManuallyActivated(false);
      return;
    }
    setLoadingPlans(true);
    listPlans()
      .then((res) => {
        const active = res.plans.filter((p) => p.active);
        setPlans(active);
        setPlanId((prev) => prev || active[0]?.id || '');
      })
      .catch((e) =>
        setError(translateApiError(e, t, { fallback: t('assignPlanDialog.loadPlansError') })),
      )
      .finally(() => setLoadingPlans(false));
  }, [open, t]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (!org) return;
    event.preventDefault();
    if (!planId) {
      setError(t('assignPlanDialog.selectPlan'));
      return;
    }

    const trimmedEmail = initialAdminEmail.trim();
    const trimmedName = initialAdminName.trim();
    let hasFieldError = false;

    if (!trimmedEmail) {
      setEmailError(t('assignPlanDialog.emailRequired'));
      hasFieldError = true;
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError(t('assignPlanDialog.emailInvalid'));
      hasFieldError = true;
    } else {
      setEmailError(null);
    }

    if (!trimmedName) {
      setNameError(t('assignPlanDialog.nameRequired'));
      hasFieldError = true;
    } else {
      setNameError(null);
    }

    if (hasFieldError) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await assignPlanToOrganization(org.id, {
        planId,
        initialAdminEmail: trimmedEmail,
        initialAdminName: trimmedName,
        // Omitted when 'automatic' (the default) — mirrors the shared
        // request schema's "optional, defaults to automatic downstream"
        // contract so every existing (automatic-only) caller/test is
        // byte-for-byte unaffected.
        ...(collectionMethod === 'manual' ? { collectionMethod: 'manual' as const } : {}),
      });
      if (result.setupUrl) {
        setSetupUrl(result.setupUrl);
      } else if (result.collectionMethod === 'manual') {
        setManuallyActivated(true);
      }
      onAssigned(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(translateApiError(err, t, { fallback: t('assignPlanDialog.assignError') }));
      } else {
        setError(translateApiError(err, t, { fallback: t('assignPlanDialog.assignError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('assignPlanDialog.title')}</DialogTitle>
          <DialogDescription>
            {org ? (
              <Trans
                t={t}
                i18nKey="assignPlanDialog.description"
                values={{ name: org.name }}
                components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
              />
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {setupUrl ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-sm border border-jq-success/30 bg-jq-success/5 px-4 py-3 text-[13px] text-jq-charcoal">
              {t('assignPlanDialog.setupRequiredNotice')}
            </div>
            <p className="break-all rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-3 py-2 font-mono text-[12px] text-jq-charcoal">
              {setupUrl}
            </p>
            <DialogFooter>
              <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
                {t('assignPlanDialog.done')}
              </Button>
            </DialogFooter>
          </div>
        ) : manuallyActivated ? (
          // manual-subscription-billing (WU8) — mirrors the setupUrl branch
          // above, but for the manual (offline/invoice) collection method:
          // the subscription is active immediately and no card-setup email
          // was sent (REQ-2.1/REQ-2.4).
          <div className="mt-6 space-y-4">
            <div className="rounded-sm border border-jq-success/30 bg-jq-success/5 px-4 py-3 text-[13px] text-jq-charcoal">
              {t('assignPlanDialog.manualActivatedNotice')}
            </div>
            <DialogFooter>
              <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
                {t('assignPlanDialog.done')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // noValidate: our JS validation (empty + email format) is the single
          // source of truth — the native type="email" constraint would block
          // submit for a malformed address so handleSubmit never runs.
          <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="assign-plan-select">{t('assignPlanDialog.planLabel')}</Label>
              <select
                id="assign-plan-select"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                disabled={submitting || loadingPlans}
                className="h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingPlans && <option value="">{t('assignPlanDialog.loadingPlans')}</option>}
                {!loadingPlans && plans.length === 0 && (
                  <option value="">{t('assignPlanDialog.noActivePlans')}</option>
                )}
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* manual-subscription-billing (WU8) — collection method toggle
                (design §1/§4, spec Capability 1/2). Super_admin-only, set at
                assignment time only. */}
            <div className="space-y-1.5">
              <Label htmlFor="assign-plan-collection-method">
                {t('assignPlanDialog.collectionMethodLabel')}
              </Label>
              <select
                id="assign-plan-collection-method"
                value={collectionMethod}
                onChange={(e) => setCollectionMethod(e.target.value as CollectionMethodDto)}
                disabled={submitting}
                className="h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="automatic">
                  {t('assignPlanDialog.collectionMethod.automatic')}
                </option>
                <option value="manual">{t('assignPlanDialog.collectionMethod.manual')}</option>
              </select>
              <p className="text-[12px] text-jq-graphite">
                {collectionMethod === 'manual'
                  ? t('assignPlanDialog.collectionMethodHelp.manual')
                  : t('assignPlanDialog.collectionMethodHelp.automatic')}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="initial-admin-email">
                {t('assignPlanDialog.initialAdminEmailLabel')}
              </Label>
              <Input
                id="initial-admin-email"
                type="email"
                placeholder={t('assignPlanDialog.initialAdminEmailPlaceholder')}
                value={initialAdminEmail}
                onChange={(e) => {
                  setInitialAdminEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                disabled={submitting}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={
                  emailError ? 'initial-admin-email-error' : 'initial-admin-email-help'
                }
              />
              {emailError ? (
                <p
                  id="initial-admin-email-error"
                  role="alert"
                  className="text-[12px] text-jq-danger"
                >
                  {emailError}
                </p>
              ) : (
                <p id="initial-admin-email-help" className="text-[12px] text-jq-graphite">
                  {t('assignPlanDialog.initialAdminEmailHelp')}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="initial-admin-name">
                {t('assignPlanDialog.initialAdminNameLabel')}
              </Label>
              <Input
                id="initial-admin-name"
                placeholder={t('assignPlanDialog.initialAdminNamePlaceholder')}
                value={initialAdminName}
                onChange={(e) => {
                  setInitialAdminName(e.target.value);
                  if (nameError) setNameError(null);
                }}
                disabled={submitting}
                aria-invalid={nameError ? true : undefined}
              />
              {nameError && (
                <p role="alert" className="text-[12px] text-jq-danger">
                  {nameError}
                </p>
              )}
            </div>

            {error && (
              <p className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {t('assignPlanDialog.cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={submitting || !planId}>
                {submitting ? t('assignPlanDialog.submitting') : t('assignPlanDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
